import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { mobileWidth } from '@/constants';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useAccountDetailsFlyout } from '@/hooks/useAccountDetailsFlyout';
import { AccountCategory } from '@/interfaces/balanceSheet';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { useGetAccountsQuery } from '@/store/accountsApi';
import { accountsToBalanceData } from '@/utils/accountsToBalanceData';
import { formatNumberToCurrency } from '@/utils/currency';
import { useLocalization } from '@/hooks/useLocalization';
import { useEffectiveExclusions } from '@/hooks/useEffectiveExclusions';
import { ContentContainer, ContentContainerVariant } from '../contentContainer';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { PrintButton } from '@/components/common/PrintButton';
import { AccountBase } from '@/models/AccountViewModel';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';

type ExpandLevel = 'none' | 'category' | 'subcategory';

interface PersonalFinancialStatementProps extends StandardWidgetProps {
  asOfDate?: string;
  investorId?: number;
  defaultExpandLevel?: ExpandLevel;
}

interface PersonalFinancialStatementSkeletonProps {
  hideTitle?: boolean;
  widgetTitle: string;
  container: ContentContainerVariant;
  footer?: ReactNode;
  action?: ReactNode;
  contentRef?: React.RefObject<HTMLDivElement>;
}

const PersonalFinancialStatementContainer = ({
  container,
  hideTitle,
  widgetTitle,
  children,
  footer,
  action,
  contentRef,
}: PropsWithChildren<PersonalFinancialStatementSkeletonProps>) => {
  return (
    <ContentContainer
      container={container}
      hasCustomPadding
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,

        height: 'fit-content',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      {!hideTitle && action && (
        <Box
          sx={{
            position: 'absolute',
            top: 24,
            right: 24,
            zIndex: 1,
          }}
        >
          {action}
        </Box>
      )}
      <Box ref={contentRef}>
        {!hideTitle && (
          <Box p={3}>
            <Typography
              variant='h5'
              sx={{ mb: 3, textAlign: 'left' }}
            >
              {widgetTitle}
            </Typography>
          </Box>
        )}

        <Box>{children}</Box>
        {footer}
      </Box>
    </ContentContainer>
  );
};

export default function PersonalFinancialStatement({
  asOfDate,
  investorId,
  onDataLoaded,
  hideTitle,
  defaultExpandLevel,
  title,
  container = 'widget',
  isHalfWidth = false,
}: PersonalFinancialStatementProps) {
  const authIsLoaded = useAuthIsLoaded();
  const isMobile = useMediaQuery(mobileWidth);
  const { openAccountById, canOpenAccount, accountFlyout } =
    useAccountDetailsFlyout({ investorId, onlyPrimaryInvestors: false });
  const { t } = useLocalization();
  const widgetTitle = title ?? t('personalFinancialStatement.title');
  const contentRef = useRef<HTMLDivElement>(null);

  const effectiveExclusions = useEffectiveExclusions();

  const {
    data: accountsData,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetAccountsQuery(
    authIsLoaded
      ? {
          showClosed: false,
          asOfDate,
          ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
          OnlyPrimaryInvestors: false,
          ...(effectiveExclusions.excludedAccounts.length > 0
            ? { ExcludedAccounts: effectiveExclusions.excludedAccounts }
            : {}),
          ...(effectiveExclusions.excludedAccountTypes.length > 0
            ? { ExcludedAccountTypes: effectiveExclusions.excludedAccountTypes }
            : {}),
          ...(effectiveExclusions.excludedCategories.length > 0
            ? { ExcludedCategories: effectiveExclusions.excludedCategories }
            : {}),
          ...(effectiveExclusions.excludedInstitutions.length > 0
            ? { ExcludedInstitutions: effectiveExclusions.excludedInstitutions }
            : {}),
        }
      : skipToken,
  );

  const balanceData = useMemo(() => {
    if (!accountsData) return null;
    const { Records } = accountsData;
    return accountsToBalanceData(Records, 'classification');
  }, [accountsData]);

  const { assets, liabilities, netWorth } = useMemo(() => {
    if (!balanceData) {
      return {
        assets: { total: 0, categories: [] },
        liabilities: { total: 0, categories: [] },
        netWorth: 0,
      };
    }

    const { assets, liabilities } = balanceData;

    return {
      assets,
      liabilities,
      netWorth: assets.total + liabilities.total,
    };
  }, [balanceData]);

  useEffect(() => {
    if (accountsData && !isFetching) {
      onDataLoaded?.();
    }
  }, [accountsData, isFetching, onDataLoaded]);

  // Collapsed unless the caller asks for more: 'category' opens the top-level
  // sections, 'subcategory' also opens each category.
  const sectionsExpandedByDefault =
    defaultExpandLevel === 'category' || defaultExpandLevel === 'subcategory';
  const categoriesExpandedByDefault = defaultExpandLevel === 'subcategory';

  const renderAccountGroup = (category: AccountCategory, index: number) => {
    const accordionKey = `${category.name}-${String(index)}`;
    const commonProps: {
      defaultExpanded: boolean;
      disableGutters?: boolean;
    } = {
      defaultExpanded: categoriesExpandedByDefault,
    };

    if (!isMobile) {
      commonProps.disableGutters = true;
    }

    const openAccountDetailsFlyout = (account: AccountBase) => {
      if (!canOpenAccount(account.AccountId)) {
        return;
      }

      openAccountById(account.AccountId);
    };

    return (
      <Accordion
        elevation={0}
        key={accordionKey}
        {...commonProps}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            flexDirection: 'row-reverse', // Moves the icon to the left
            '& .MuiAccordionSummary-expandIconWrapper': {
              marginRight: 1, // Adds spacing between the icon and the text
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              width: '100%',
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }}>{category.name}</Typography>
            <Typography
              sx={{
                fontWeight: 'bold',
              }}
            >
              {formatNumberToCurrency(category.total)}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ fontSize: '14px' }}>
          <Box>
            {category.accounts.map((account, accountIndex) => {
              const isClickable = canOpenAccount(account.AccountId);
              return (
                <Box key={`${String(account.AccountId)}-${String(index)}`}>
                  <Box
                    onClick={
                      isClickable
                        ? () => {
                            openAccountDetailsFlyout(account);
                          }
                        : undefined
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: isClickable ? 'pointer' : 'default',
                      gap: 2,
                      py: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {account.Name}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                      >
                        {account.InstitutionName}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        color={
                          account.Balance < 0 ? 'error.main' : 'text.primary'
                        }
                      >
                        {formatNumberToCurrency(account.Balance)}
                      </Typography>
                    </Box>
                  </Box>
                  {accountIndex < category.accounts.length - 1 && <Divider />}
                </Box>
              );
            })}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSection = (
    title: string,
    data: { total: number; categories: AccountCategory[] },
  ) => {
    return (
      <Accordion
        defaultExpanded={sectionsExpandedByDefault}
        elevation={0}
        disableGutters={!isMobile}
      >
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          sx={{ p: 0 }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }}>{title}</Typography>
            <Typography sx={{ fontWeight: 'bold' }}>
              {formatNumberToCurrency(data.total)}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {data.categories.map(renderAccountGroup)}
        </AccordionDetails>
      </Accordion>
    );
  };

  if ((isLoading || isFetching) && !accountsData) {
    return (
      <PersonalFinancialStatementContainer
        widgetTitle={widgetTitle}
        container={container}
        hideTitle={hideTitle}
      >
        <Grid
          container
          spacing={4}
          sx={{
            p: 3,
            pt: 0,
          }}
        >
          {Array.from(new Array(2)).map((_, groupIndex) => (
            <Grid
              size={{ xs: 12, sm: 6 }}
              key={`assets-group-${String(groupIndex)}`}
            >
              {Array.from(new Array(4)).map((_, index) => (
                <Skeleton
                  key={`assets-${String(index)}`}
                  width='100%'
                  height={48}
                />
              ))}
            </Grid>
          ))}
        </Grid>
      </PersonalFinancialStatementContainer>
    );
  }

  if (isError || !balanceData) {
    const errorMessage =
      error && typeof error === 'object' && 'data' in error
        ? ((error as { data?: { message?: string } }).data?.message ??
          t('Error loading personal financial statement data.'))
        : t('Error loading personal financial statement data.');

    return (
      <PersonalFinancialStatementContainer
        widgetTitle={widgetTitle}
        container={container}
        hideTitle={hideTitle}
      >
        <Grid
          container
          spacing={4}
          sx={{ p: 3, pt: 0 }}
        >
          <Typography>{errorMessage}</Typography>
        </Grid>
      </PersonalFinancialStatementContainer>
    );
  }

  return (
    <>
      <PersonalFinancialStatementContainer
        widgetTitle={widgetTitle}
        container={container}
        action={<PrintButton contentRef={contentRef} />}
        contentRef={contentRef}
        footer={
          <Box
            sx={(theme: Theme) => ({
              borderTop: `1px solid ${theme.palette.divider}`,
              p: 3,
            })}
          >
            <Stack
              flexDirection='row'
              gap={4}
              justifyContent='space-between'
            >
              <Typography sx={{ fontWeight: 'bold' }}>
                {t('netWorth')}:
              </Typography>
              <Typography sx={{ fontWeight: 'bold' }}>
                {formatNumberToCurrency(netWorth)}
              </Typography>
            </Stack>
          </Box>
        }
      >
        <Box sx={{ p: 3 }}>
          <Grid
            container
            columnSpacing={8}
            rowSpacing={{ sm: 1 }}
          >
            <Grid size={{ xs: 12, lg: isHalfWidth ? 12 : 6 }}>
              {renderSection(t('assets'), assets)}
            </Grid>
            <Grid size={{ xs: 12, lg: isHalfWidth ? 12 : 6 }}>
              {renderSection(t('liabilities'), liabilities)}
            </Grid>
          </Grid>
        </Box>
      </PersonalFinancialStatementContainer>
      {accountFlyout}
    </>
  );
}
